import axios, {
  AxiosInstance,
  AxiosRequestHeaders,
  InternalAxiosRequestConfig,
} from 'axios';
import { instrumentAxios } from '../shared/honeycomb/axiosHoneyCombTelemetry';
import { sessionRefresh } from './Auth/Auth.service';
import {
  CLAIMSCORE_API_URL,
  getAuthorizationToken,
  getBaseURLWithVersion,
} from './clientHelpers';
import { ClaimScoreApiVersion } from './types';
import { isRefreshRequest, isSessionExpired } from './utils';

let refreshingPromise: Promise<void> | null = null;

function refreshSession(): Promise<void> {
  if (refreshingPromise) return refreshingPromise;

  refreshingPromise = (async () => {
    try {
      await sessionRefresh();
    } finally {
      refreshingPromise = null;
    }
  })();
  return refreshingPromise;
}

export const createClaimScoreApiClient = (
  version: ClaimScoreApiVersion | null = ClaimScoreApiVersion.V2,
): AxiosInstance => {
  const client: AxiosInstance = axios.create({
    baseURL: version ? getBaseURLWithVersion(version) : CLAIMSCORE_API_URL,
    withCredentials: true,
  });

  instrumentAxios(client, {
    tracerName: 'claimscore-api-service',
  });

  client.interceptors.request.use(
    async (config) => {
      const token = await getAuthorizationToken();

      config.headers ??= {} as AxiosRequestHeaders;
      config.headers.Authorization = `${token}`;
      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalConfig = error.config as InternalAxiosRequestConfig;
      if (
        !originalConfig ||
        !isSessionExpired(error) ||
        isRefreshRequest(originalConfig)
      ) {
        return Promise.reject(error);
      }

      try {
        await refreshSession();
        return client.request(originalConfig);
      } catch {
        return Promise.reject(error);
      }
    },
  );

  return client;
};

const claimScoreApiServiceClient: AxiosInstance = createClaimScoreApiClient(
  ClaimScoreApiVersion.V2,
);

export default claimScoreApiServiceClient;
